import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { AlertCircle, ArrowUpRight, CheckCircle2, ChevronRight, Filter, MapPin, Search, Sparkles, Users, Zap } from "lucide-react";
import { OPPORTUNITIES, AI_MATCHING, shortIdr, idr } from "../data/kabelul";

const TYPES = ["All", "Individual Job", "Freelance", "Project-based"];

export default function OpportunitiesPage({ setPage }) {
  const [tab, setTab] = useState("All");
  const [query, setQuery] = useState("");
  const [showOnlyEligible, setShowOnlyEligible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [appliedTo, setAppliedTo] = useState(null);

  const filtered = useMemo(() => {
    return OPPORTUNITIES.filter((o) => {
      if (tab !== "All" && !o.type.includes(tab)) return false;
      if (showOnlyEligible && !o.eligible) return false;
      if (query && !(`${o.title} ${o.company} ${o.location}`.toLowerCase().includes(query.toLowerCase()))) return false;
      return true;
    });
  }, [tab, query, showOnlyEligible]);

  const eligibleCount = OPPORTUNITIES.filter((o) => o.eligible).length;

  return (
    <div className="page-inner">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="page-intro">
        <div>
          <p className="eyebrow">FIND WORK · MATCHED TO YOUR PASSPORT</p>
          <h1>Opportunities</h1>
          <p className="subtitle">KABELUL AI matches every opportunity against your verified skills.</p>
        </div>
        <div className="k-eligibility-hero" data-testid="opportunities-eligibility-hero">
          <Sparkles size={18} />
          <div>
            <b>You're eligible for {AI_MATCHING.eligible} opportunities</b>
            <small>Complete recommended courses to unlock <b>{AI_MATCHING.potentialIfLearn.reduce((s, x) => s + x.unlocks, 0)} more</b></small>
          </div>
        </div>
      </motion.div>

      <div className="k-toolbar">
        <div className="k-search">
          <Search size={15} />
          <input
            data-testid="opportunities-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, company, or location"
          />
        </div>
        <div className="k-tabs">
          {TYPES.map((t) => (
            <button
              key={t}
              data-testid={`opportunities-tab-${t.toLowerCase().replaceAll(" ", "-")}`}
              className={t === tab ? "k-tab active" : "k-tab"}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ))}
        </div>
        <label className="k-toggle">
          <input
            type="checkbox"
            checked={showOnlyEligible}
            onChange={(e) => setShowOnlyEligible(e.target.checked)}
            data-testid="opportunities-eligible-toggle"
          />
          <span>Show only eligible ({eligibleCount})</span>
        </label>
      </div>

      <div className="k-opp-grid">
        {filtered.map((o, i) => (
          <button
            key={o.id}
            className={`k-opp-card ${o.eligible ? "" : "locked"}`}
            data-testid={`opportunity-card-${i}`}
            onClick={() => setSelected(o)}
          >
            <div className="k-opp-card-head">
              <div className={`k-opp-avatar ${o.tone}`}>{o.companyInitials}</div>
              <div className="k-opp-card-title">
                <b>{o.title}</b>
                <small>{o.company}</small>
              </div>
              {o.eligible ? (
                <span className="k-badge-eligible"><CheckCircle2 size={12} /> Eligible</span>
              ) : (
                <span className="k-badge-locked"><AlertCircle size={12} /> Missing skills</span>
              )}
            </div>
            <div className="k-opp-card-meta">
              <span><MapPin size={11} /> {o.location}</span>
              <span className="k-tag">{o.type}</span>
              <span>{o.duration}</span>
              <span><Users size={11} /> {o.applicants} applied</span>
            </div>
            <div className="k-opp-card-foot">
              <div>
                <small>Budget</small>
                <b>{shortIdr(o.budget)}</b>
                <small className="unit">{o.budgetUnit}</small>
              </div>
              <div className="k-match-block">
                <Zap size={12} /> {o.matchScore}% match
              </div>
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <div className="modal-backdrop" onClick={() => setSelected(null)}>
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="modal k-modal" onClick={(e) => e.stopPropagation()} role="dialog">
            <div className="modal-head">
              <div>
                <p className="eyebrow">OPPORTUNITY · {selected.type.toUpperCase()}</p>
                <h2>{selected.title}</h2>
                <p className="muted">{selected.company} · {selected.location} · Posted {selected.posted}</p>
              </div>
              <button data-testid="close-opportunity-modal" className="icon-button" onClick={() => setSelected(null)}>×</button>
            </div>

            <div className={`k-ai-panel ${selected.eligible ? "ok" : "warn"}`}>
              <div className="k-ai-icon"><Sparkles size={17} /></div>
              <div>
                <small>KABELUL AI MATCH · {selected.matchScore}%</small>
                <p>{selected.aiReason}</p>
              </div>
            </div>

            <div className="k-opp-detail-grid">
              <div>
                <small>BUDGET</small>
                <b>{idr(selected.budget)}</b>
                <small className="unit">{selected.budgetUnit}</small>
              </div>
              <div>
                <small>DURATION</small>
                <b>{selected.duration}</b>
              </div>
              <div>
                <small>START DATE</small>
                <b>{new Date(selected.startDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</b>
              </div>
              <div>
                <small>APPLICANTS</small>
                <b>{selected.applicants} so far</b>
              </div>
            </div>

            <div className="k-section">
              <b>Description</b>
              <p>{selected.description}</p>
            </div>

            <div className="k-section">
              <b>Required skills</b>
              <div className="k-chip-row">
                {selected.requiredSkills.map((s) => {
                  const missing = selected.missingSkills.includes(s);
                  return (
                    <span className={missing ? "k-chip missing" : "k-chip ok"} key={s}>
                      {missing ? <AlertCircle size={11} /> : <CheckCircle2 size={11} />} {s}
                    </span>
                  );
                })}
              </div>
            </div>

            {!selected.eligible && (
              <div className="k-unlock-inline">
                <span>Unlock this opportunity by completing:</span>
                <button className="quiet-button" onClick={() => { setSelected(null); setPage("learning"); }} data-testid="opportunity-open-learning">
                  {selected.missingSkills[0]} <ArrowUpRight size={12} />
                </button>
              </div>
            )}

            <div className="modal-actions">
              <button className="secondary-button" onClick={() => setSelected(null)} data-testid="opportunity-cancel">Close</button>
              {selected.eligible ? (
                <button className="primary-button" onClick={() => { setAppliedTo(selected); setSelected(null); }} data-testid="opportunity-apply">
                  Apply now <ChevronRight size={14} />
                </button>
              ) : (
                <button className="dark-button" onClick={() => { setSelected(null); setPage("learning"); }} data-testid="opportunity-go-learn">
                  Go to Learning <ChevronRight size={14} />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {appliedTo && (
        <div className="modal-backdrop" onClick={() => setAppliedTo(null)}>
          <motion.div initial={{ opacity: 0, scale: .95 }} animate={{ opacity: 1, scale: 1 }} className="modal k-modal k-success-modal" onClick={(e) => e.stopPropagation()} role="dialog" data-testid="opportunity-apply-success">
            <div className="k-success-icon"><CheckCircle2 size={38} /></div>
            <p className="eyebrow" style={{ textAlign: "center" }}>APPLICATION STATUS · SUBMITTED</p>
            <h2 style={{ textAlign: "center", margin: "6px 0 8px" }}>Application sent successfully</h2>
            <p className="k-success-sub">Your application has been sent to <b>{appliedTo.company}</b>. Your KABELUL Qualification Passport has been shared with the employer.</p>
            <div className="k-success-meta">
              <div><small>ROLE</small><b>{appliedTo.title}</b></div>
              <div><small>SUBMITTED</small><b>19 Aug 2026, 09:42</b></div>
            </div>
            <div className="modal-actions" style={{ justifyContent: "center" }}>
              <button className="secondary-button" onClick={() => setAppliedTo(null)} data-testid="opportunity-success-close">Close</button>
              <button className="primary-button" onClick={() => { setAppliedTo(null); setPage("reputation"); }} data-testid="opportunity-success-track">
                Track applications <ChevronRight size={14} />
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
